import { Button, Modal } from "@shared/ui";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { AttachTaskCreator } from "../lib/attachTask";
import { useAttachScriptModal } from "../lib/useAttachScriptModal";
import { AttachBriefEditorStep } from "./AttachBriefEditorStep";
import { AttachTaskChrome } from "./AttachTaskChrome";
import { AttachTaskDeliverableStep } from "./AttachTaskDeliverableStep";
import styles from "./AttachTaskModal.module.scss";

interface AttachScriptModalProps {
  open: boolean;
  selectedCount: number;
  creators: AttachTaskCreator[];
  confirmLoading?: boolean;
  onClose: () => void;
  onConfirm: (taskId: string, copyFromScriptId: string) => void;
}

export function AttachScriptModal({
  open,
  selectedCount,
  creators,
  confirmLoading,
  onClose,
  onConfirm,
}: AttachScriptModalProps) {
  const {
    step,
    setStep,
    taskId,
    setTaskId,
    scriptingId,
    editorRef,
    editorKey,
    hasContent,
    isBusy,
    isCreating,
    pendingCreate,
    canContinue,
    deliverableOptions,
    selectedDeliverable,
    skippedCount,
    syncContent,
    handleMediaUploadStart,
    handleMediaUploadComplete,
    handleImportComplete,
    handleContinue,
    handleAttach,
  } = useAttachScriptModal({
    open,
    selectedCount,
    creators,
    confirmLoading,
    onConfirm,
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={680}
      zIndex={1100}
      classNames={{
        header: styles.modalHeader,
        body: styles.modalBody,
        footer: styles.modalFooter,
      }}
      title={
        <div className={styles.titleBlock}>
          <span className={styles.title}>Attach script</span>
          <span className={styles.subtitle}>
            {step === 1
              ? "Choose which deliverable this script applies to."
              : "Write the script, then attach it to the selected creators."}
          </span>
        </div>
      }
      footer={
        <div className={styles.footer}>
          <div>
            {step === 2 ? (
              <Button
                type="text"
                onClick={() => setStep(1)}
                className={styles.backButton}
              >
                <ArrowLeft size={14} />
                Back
              </Button>
            ) : null}
          </div>
          <div className={styles.footerActions}>
            <Button onClick={onClose}>Cancel</Button>
            {step === 1 ? (
              <Button
                type="primary"
                disabled={!canContinue}
                loading={isCreating || pendingCreate}
                onClick={handleContinue}
                className={styles.primaryButton}
              >
                Continue
                <ArrowRight size={14} />
              </Button>
            ) : (
              <Button
                type="primary"
                loading={isBusy}
                disabled={!taskId || !scriptingId || isBusy || !hasContent}
                onClick={handleAttach}
                className={styles.primaryButton}
              >
                Attach to {selectedCount} creator
                {selectedCount === 1 ? "" : "s"}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <AttachTaskChrome
        step={step}
        selectedCount={selectedCount}
        creators={creators}
        stepTwoLabel="Script"
      />

      {step === 1 ? (
        <AttachTaskDeliverableStep
          options={deliverableOptions}
          selectedTaskId={taskId}
          creators={creators}
          selectedCount={selectedCount}
          skippedCount={skippedCount}
          onSelect={setTaskId}
          emptyLabel="No script tasks are configured for this campaign."
          skipActionLabel="Script"
        />
      ) : (
        <AttachBriefEditorStep
          editorRef={editorRef}
          editorKey={editorKey}
          editorTitle="Script"
          deliverableName={selectedDeliverable?.name || ""}
          attachCount={selectedCount}
          onContentChange={syncContent}
          onMediaUploadStart={handleMediaUploadStart}
          onMediaUploadComplete={handleMediaUploadComplete}
          onImportComplete={handleImportComplete}
        />
      )}
    </Modal>
  );
}
